import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, MapPin, Phone, Mail, Instagram, Youtube, Smartphone, Star } from 'lucide-react';
import { STUDIO, SOCIALS } from '../data/mock';

const NAV_LINKS = [
  { to: '/the-workout', label: 'The Workout' },
  { to: '/hyrox-classes-dallas', label: 'HYROX' },
  { to: '/hiit-workout/schedule', label: 'Schedule' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/buy', label: 'Buy' },
  { to: '/hiit-classes-dallas', label: 'Location' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
];

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title ? `${title} | PITFIT — HIIT Workout Studio in Uptown Dallas` : 'PITFIT — HIIT Workout Studio in Uptown Dallas | Get In The PIT';
    if (description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = description;
    }
  }, [title, description]);
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 frosted border-b transition-colors duration-300 ${scrolled ? 'border-[rgba(148,170,220,0.16)]' : 'border-transparent'}`}>
      <nav aria-label="Main" className="max-w-7xl mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0" aria-label="PITFIT home">
          <img src="/logo-white.png" alt="PITFIT" className="h-8 md:h-9 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/book" className="btn-primary !hidden sm:!inline-flex !min-h-[44px] !py-2.5 !px-5 text-[15px]" data-testid="nav-book-cta">
            Book a Class <ArrowRight size={16} />
          </Link>
          <button
            className="lg:hidden p-2.5 text-white rounded-lg border border-[rgba(148,170,220,0.22)]"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden frosted border-t border-[rgba(148,170,220,0.13)] px-5 pb-6 pt-2">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `block py-3.5 text-lg font-display font-semibold border-b border-[rgba(148,170,220,0.08)] ${isActive ? 'text-[#7DA7FF]' : 'text-white'}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex flex-col gap-2 mt-4">
            <Link to="/hiit-workout/first-class" className="nav-link py-1">Your First Class</Link>
            <Link to="/results" className="nav-link py-1">Results</Link>
            <Link to="/blog" className="nav-link py-1">Blog</Link>
            <Link to="/shop" className="nav-link py-1">Shop</Link>
            <Link to="/contact" className="nav-link py-1">Contact</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileStickyCTA() {
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (location.pathname === '/book' || !show) return null;
  return (
    <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 frosted border-t border-[rgba(148,170,220,0.16)] px-4 py-3" role="complementary" aria-label="Quick booking" data-testid="mobile-sticky-cta">
      <Link to="/book" className="btn-primary w-full">
        Book a Class <ArrowRight size={17} />
      </Link>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[rgba(148,170,220,0.13)] mt-4" style={{ background: 'var(--pf-bg2)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="inline-flex" aria-label="PITFIT home">
            <img src="/logo-white.png" alt="PITFIT" className="h-9 w-auto" />
          </Link>
          <p className="text-muted-pf mt-4 text-[15px] leading-relaxed">
            Coached, non-stop HIIT and HYROX training in a Travis Walk parking garage. 45- and 60-minute classes for every level.
          </p>
          <div className="flex items-center gap-2 mt-5 text-[14px]" style={{ color: 'var(--pf-brand2)' }}>
            <Star size={15} fill="currentColor" />
            <span className="font-mono-pf tracking-wide">{STUDIO.rating} · {STUDIO.reviewCount}+ REVIEWS</span>
          </div>
          <div className="flex gap-3 mt-5">
            <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" aria-label="PITFIT on Instagram" className="p-2.5 rounded-lg border border-[rgba(148,170,220,0.22)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors"><Instagram size={17} /></a>
            <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" aria-label="PITFIT on YouTube" className="p-2.5 rounded-lg border border-[rgba(148,170,220,0.22)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors"><Youtube size={17} /></a>
            <a href={SOCIALS.linktree} target="_blank" rel="noreferrer" aria-label="Get the PITFIT app" className="p-2.5 rounded-lg border border-[rgba(148,170,220,0.22)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors"><Smartphone size={17} /></a>
          </div>
        </div>

        <div>
          <h3 className="mono-label mb-5">The Workout</h3>
          <ul className="space-y-3 text-[15px]">
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/the-workout">The PITFIT Method</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/hyrox-classes-dallas">HYROX Classes Dallas</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/hiit-workout/schedule">Class Schedule</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/hiit-workout/first-class">Your First Class</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/hiit-workout/beginners">HIIT for Beginners</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/results">Results</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="mono-label mb-5">Studio</h3>
          <ul className="space-y-3 text-[15px]">
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/pricing">Memberships & Pricing</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/buy">Buy Classes & Gift Cards</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/about">Our Story</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/about/trainers">Trainers</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/about/press">Press</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/blog">Blog</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/shop">Shop</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="mono-label mb-5">Find The PIT</h3>
          <ul className="space-y-4 text-[15px] text-muted-pf">
            <li className="flex gap-3"><MapPin size={17} className="shrink-0 mt-0.5" style={{ color: 'var(--pf-brand)' }} /><span>{STUDIO.address}<br /><span className="text-[13px] font-mono-pf">{STUDIO.neighborhood}</span></span></li>
            <li className="flex gap-3"><Phone size={17} className="shrink-0 mt-0.5" style={{ color: 'var(--pf-brand)' }} /><a href="tel:+14693721085" className="hover:text-white transition-colors">{STUDIO.phone}</a></li>
            <li className="flex gap-3"><Mail size={17} className="shrink-0 mt-0.5" style={{ color: 'var(--pf-brand)' }} /><a href={`mailto:${STUDIO.email}`} className="hover:text-white transition-colors">{STUDIO.email}</a></li>
          </ul>
          <ul className="mt-5 space-y-2 text-[14px]">
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/hiit-classes-dallas">HIIT Classes Dallas</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/locations/uptown-dallas">Uptown Dallas Studio</Link></li>
            <li><Link className="text-muted-pf hover:text-white transition-colors" to="/contact">Contact</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[rgba(148,170,220,0.1)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[13px] text-muted-pf">
          <span>© {new Date().getFullYear()} PITFIT Workout · Dallas, TX. All rights reserved.</span>
          <span className="font-mono-pf tracking-wider uppercase">Get in the PIT.</span>
        </div>
      </div>
      <div className="sm:hidden h-[76px]" aria-hidden="true" />
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--pf-bg)' }}>
      <Navbar />
      <main className="flex-1 pt-[68px]">{children}</main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
